import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import { subscribeManyEpisodes } from '@/lib/collections/episodes';
import type { EpisodesResponse } from '@/lib/collections/episodes';
import { Particles } from '@/components/effects';
import { Play, X, ChevronDown, Film, Clock, Star, Home } from 'lucide-react';

// ─── Artwork constants ──────────────────────────────────────────────────────

const ARTWORK = {
  hero: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-697d5189a1e3dd2cc1a82d2b/69c35b01a4a5c1c8366b9d23',
  char1: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69d4ab49af39b1de2fd5bf24',
  char2: 'https://tarobase-app-storage-public-v2-prod.s3.amazonaws.com/tarobase-app-storage-69c35bd375e1260e73d99017/69d4ab49af39b1de2fd5bf24',
};

// ─── YouTube embed helpers ───────────────────────────────────────────────────

function getYouTubeEmbedUrl(url: string): string | null {
  if (!url) return null;
  if (url.includes('/embed/')) return url;
  const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|shorts\/))([a-zA-Z0-9_-]{11})/);
  if (youtubeMatch) return `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&rel=0`;
  return null;
}

// ─── Video Modal ─────────────────────────────────────────────────────────────

const VideoModal: React.FC<{
  episode: EpisodesResponse;
  onClose: () => void;
}> = ({ episode, onClose }) => {
  const embedUrl = getYouTubeEmbedUrl(episode.videoUrl);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.95)', backdropFilter: 'blur(8px)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl"
        onClick={e => e.stopPropagation()}
        style={{
          border: '2px solid rgba(124,252,0,0.3)',
          boxShadow: '0 0 60px rgba(124,252,0,0.15), 0 0 0 1px rgba(124,252,0,0.1)',
        }}
      >
        {/* Header bar */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ background: '#0a0f1a', borderBottom: '1px solid rgba(124,252,0,0.2)' }}
        >
          <div style={{ fontFamily: "'Bangers', cursive", letterSpacing: '0.1em' }}>
            <span style={{ color: '#7CFC00', fontSize: '0.75rem' }}>S{episode.season} E{episode.episodeNumber}</span>
            <span style={{ color: '#ffffff', fontSize: '1.1rem', marginLeft: '10px' }}>{episode.title}</span>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded transition-colors"
            style={{ background: 'rgba(230,57,70,0.15)', border: '1px solid rgba(230,57,70,0.4)', color: '#E63946' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(230,57,70,0.3)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(230,57,70,0.15)'; }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Video area */}
        <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
          {embedUrl ? (
            <iframe
              src={embedUrl}
              title={episode.title}
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
              style={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%', border: 'none',
              }}
            />
          ) : (
            <video
              src={episode.videoUrl}
              controls
              autoPlay
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
            />
          )}
        </div>

        {/* Description */}
        {episode.description && (
          <div
            className="px-4 py-3"
            style={{ background: '#0a0f1a', borderTop: '1px solid rgba(124,252,0,0.1)' }}
          >
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.875rem', fontFamily: "'Barlow', sans-serif" }}>
              {episode.description}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Episode Card ─────────────────────────────────────────────────────────────

const EpisodeCard: React.FC<{
  episode: EpisodesResponse;
  onClick: () => void;
  index: number;
}> = ({ episode, onClick, index }) => {
  const [hovered, setHovered] = useState(false);
  const fallbackImages = [ARTWORK.hero, ARTWORK.char1, ARTWORK.char2];
  const fallback = fallbackImages[index % 3];

  return (
    <div
      className="cursor-pointer group animate-fade-in-up"
      style={{
        animationDelay: `${index * 0.07}s`,
        animationFillMode: 'both',
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          border: hovered ? '2px solid #7CFC00' : '2px solid rgba(255,255,255,0.08)',
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          transform: hovered ? 'translateY(-4px) scale(1.02)' : 'translateY(0) scale(1)',
          boxShadow: hovered ? '0 12px 40px rgba(124,252,0,0.2), 0 0 0 1px rgba(124,252,0,0.1)' : '0 4px 20px rgba(0,0,0,0.5)',
          background: '#0a0f1a',
          overflow: 'hidden',
        }}
      >
        {/* Thumbnail */}
        <div style={{ position: 'relative', paddingBottom: '56.25%', overflow: 'hidden', background: '#050a14' }}>
          <img
            src={episode.thumbnailUrl || fallback}
            alt={episode.title}
            onError={e => { (e.currentTarget as HTMLImageElement).src = fallback; }}
            style={{
              position: 'absolute', top: 0, left: 0,
              width: '100%', height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.4s ease',
              transform: hovered ? 'scale(1.06)' : 'scale(1)',
            }}
          />
          {/* Dark overlay */}
          <div style={{
            position: 'absolute', inset: 0,
            background: hovered
              ? 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)'
              : 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 70%)',
            transition: 'background 0.3s ease',
          }} />

          {/* Play button */}
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: hovered ? 1 : 0,
            transition: 'opacity 0.2s ease',
          }}>
            <div style={{
              width: 52, height: 52,
              borderRadius: '50%',
              background: 'rgba(124,252,0,0.9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 30px rgba(124,252,0,0.5)',
            }}>
              <Play size={22} fill="#0a0f1a" style={{ color: '#0a0f1a', marginLeft: 3 }} />
            </div>
          </div>

          {/* Episode badge */}
          <div style={{
            position: 'absolute', top: 8, left: 8,
            fontFamily: "'Bangers', cursive",
            fontSize: '0.75rem', letterSpacing: '0.1em',
            background: 'rgba(0,0,0,0.75)',
            border: '1px solid rgba(124,252,0,0.4)',
            color: '#7CFC00',
            padding: '2px 8px',
          }}>
            E{episode.episodeNumber}
          </div>
        </div>

        {/* Info */}
        <div style={{ padding: '12px 14px 14px' }}>
          <h3 style={{
            fontFamily: "'Bangers', cursive",
            fontSize: '1.1rem',
            letterSpacing: '0.06em',
            color: '#ffffff',
            marginBottom: 4,
            lineHeight: 1.2,
            transition: 'color 0.2s ease',
            ...(hovered ? { color: '#7CFC00' } : {}),
          }}>
            {episode.title}
          </h3>
          {episode.description && (
            <p style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.5,
              overflow: 'hidden',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}>
              {episode.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Hero Section ─────────────────────────────────────────────────────────────

const HeroFeatured: React.FC<{
  episode: EpisodesResponse | null;
  onWatch: (ep: EpisodesResponse) => void;
}> = ({ episode, onWatch }) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handler = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  if (!episode) {
    // Coming soon state
    return (
      <div
        style={{
          position: 'relative',
          minHeight: '70vh',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        {/* Parallax background */}
        <img
          src={ARTWORK.hero}
          alt="Danger Dan"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
            objectPosition: 'center top',
            transform: `translateY(${scrollY * 0.25}px)`,
            filter: 'brightness(0.35) saturate(1.2)',
          }}
        />

        {/* Gradient overlays */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(to top, #080d1f 0%, rgba(8,13,31,0.6) 40%, rgba(8,13,31,0.2) 100%)',
        }} />
        <div style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(ellipse at 60% 50%, rgba(124,252,0,0.04) 0%, transparent 70%)',
        }} />

        <Particles quantity={30} color="hsl(90, 100%, 54%)" />

        <div
          className="relative z-10 container"
          style={{ paddingBottom: '80px' }}
        >
          <div style={{ maxWidth: '680px' }}>
            <div style={{
              display: 'inline-block',
              fontFamily: "'Bangers', cursive",
              fontSize: '0.85rem',
              letterSpacing: '0.25em',
              color: '#7CFC00',
              border: '1px solid rgba(124,252,0,0.4)',
              padding: '4px 16px',
              marginBottom: '20px',
              background: 'rgba(124,252,0,0.05)',
            }}>
              COMING SOON
            </div>

            <h1 style={{
              fontFamily: "'Bangers', cursive",
              fontSize: 'clamp(3.5rem, 10vw, 7rem)',
              letterSpacing: '0.04em',
              lineHeight: 0.9,
              color: '#ffffff',
              textShadow: '4px 4px 0 rgba(0,0,0,0.8), 0 0 60px rgba(124,252,0,0.15)',
              marginBottom: '20px',
            }}>
              DANGER DAN
              <span style={{ display: 'block', color: '#7CFC00', WebkitTextStroke: '1px rgba(0,0,0,0.3)' }}>
                THE ANIMATED SERIES
              </span>
            </h1>

            <p style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 'clamp(1rem, 2vw, 1.25rem)',
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.6,
              marginBottom: '32px',
              maxWidth: '520px',
            }}>
              An action-packed western fantasy cartoon series coming to a universe near you.
              Check back soon for episode drops.
            </p>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div style={{
                fontFamily: "'Bangers', cursive",
                fontSize: '1.1rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.35)',
                padding: '10px 28px',
                border: '2px solid rgba(255,255,255,0.15)',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <Clock size={16} />
                EPISODES LOADING...
              </div>
            </div>
          </div>
        </div>

        {/* Bottom character art */}
        <img
          src={ARTWORK.char1}
          alt=""
          aria-hidden
          style={{
            position: 'absolute', bottom: 0, right: '5%',
            height: '80%', width: 'auto',
            objectFit: 'contain',
            opacity: 0.35,
            filter: 'saturate(0.6) brightness(0.7)',
            transform: `translateY(${scrollY * 0.1}px)`,
            pointerEvents: 'none',
          }}
        />
      </div>
    );
  }

  // Featured episode hero
  return (
    <div
      style={{
        position: 'relative',
        minHeight: '70vh',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-end',
      }}
    >
      {/* Background image */}
      <img
        src={episode.thumbnailUrl || ARTWORK.hero}
        alt={episode.title}
        onError={e => { (e.currentTarget as HTMLImageElement).src = ARTWORK.hero; }}
        style={{
          position: 'absolute', inset: 0,
          width: '100%', height: '100%',
          objectFit: 'cover',
          objectPosition: 'center top',
          transform: `translateY(${scrollY * 0.2}px)`,
          filter: 'brightness(0.4) saturate(1.1)',
        }}
      />

      {/* Gradient layers */}
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to top, #080d1f 0%, rgba(8,13,31,0.7) 40%, rgba(8,13,31,0.15) 100%)',
      }} />
      <div style={{
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to right, rgba(8,13,31,0.9) 0%, transparent 60%)',
      }} />

      {/* Content */}
      <div className="relative z-10 container" style={{ paddingBottom: '80px' }}>
        <div style={{ maxWidth: '600px' }}>
          {/* Label */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: "'Bangers', cursive",
            fontSize: '0.8rem', letterSpacing: '0.2em',
            color: '#7CFC00',
            border: '1px solid rgba(124,252,0,0.4)',
            padding: '3px 12px',
            marginBottom: '16px',
            background: 'rgba(124,252,0,0.05)',
          }}>
            <Star size={11} fill="#7CFC00" />
            FEATURED — S{episode.season} E{episode.episodeNumber}
          </div>

          <h1 style={{
            fontFamily: "'Bangers', cursive",
            fontSize: 'clamp(2.5rem, 7vw, 5rem)',
            letterSpacing: '0.04em',
            lineHeight: 0.95,
            color: '#ffffff',
            textShadow: '3px 3px 0 rgba(0,0,0,0.8)',
            marginBottom: '16px',
          }}>
            {episode.title}
          </h1>

          {episode.description && (
            <p style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: 'clamp(0.95rem, 1.8vw, 1.15rem)',
              color: 'rgba(255,255,255,0.65)',
              lineHeight: 1.6,
              marginBottom: '28px',
              maxWidth: '480px',
            }}>
              {episode.description}
            </p>
          )}

          <button
            onClick={() => onWatch(episode)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              fontFamily: "'Bangers', cursive",
              fontSize: '1.3rem',
              letterSpacing: '0.12em',
              color: '#0a0f1a',
              background: '#7CFC00',
              border: '3px solid rgba(0,0,0,0.3)',
              padding: '10px 32px',
              cursor: 'pointer',
              boxShadow: '4px 4px 0 rgba(0,0,0,0.7), 0 0 30px rgba(124,252,0,0.3)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
            }}
            onMouseEnter={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.transform = 'translateY(-2px)';
              el.style.boxShadow = '5px 6px 0 rgba(0,0,0,0.8), 0 0 40px rgba(124,252,0,0.5)';
            }}
            onMouseLeave={e => {
              const el = e.currentTarget as HTMLElement;
              el.style.transform = 'translateY(0)';
              el.style.boxShadow = '4px 4px 0 rgba(0,0,0,0.7), 0 0 30px rgba(124,252,0,0.3)';
            }}
          >
            <Play size={20} fill="#0a0f1a" />
            WATCH NOW
          </button>
        </div>
      </div>

      {/* Scroll hint */}
      <div
        style={{
          position: 'absolute', bottom: 24, right: 40,
          display: 'flex', alignItems: 'center', gap: 6,
          fontFamily: "'Bangers', cursive",
          fontSize: '0.75rem', letterSpacing: '0.2em',
          color: 'rgba(124,252,0,0.4)',
          animation: 'bounce-fade 2s ease-in-out infinite',
        }}
      >
        <ChevronDown size={14} />
        MORE EPISODES
      </div>
    </div>
  );
};

// ─── Season Section ───────────────────────────────────────────────────────────

const SeasonSection: React.FC<{
  season: number;
  episodes: EpisodesResponse[];
  onWatch: (ep: EpisodesResponse) => void;
  globalIndex: number;
}> = ({ season, episodes, onWatch, globalIndex }) => {
  const sorted = [...episodes].sort((a, b) => (a.episodeNumber as number) - (b.episodeNumber as number));

  return (
    <div style={{ marginBottom: '60px' }}>
      {/* Season header */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '16px',
          marginBottom: '24px',
        }}
      >
        <div style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '0.75rem',
          letterSpacing: '0.25em',
          color: '#7CFC00',
          background: 'rgba(124,252,0,0.08)',
          border: '1px solid rgba(124,252,0,0.3)',
          padding: '4px 14px',
          whiteSpace: 'nowrap',
        }}>
          SEASON {season}
        </div>
        <div style={{
          flex: 1,
          height: '1px',
          background: 'linear-gradient(to right, rgba(124,252,0,0.3), transparent)',
        }} />
        <span style={{
          fontFamily: "'Barlow', sans-serif",
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.3)',
          letterSpacing: '0.05em',
        }}>
          {episodes.length} episode{episodes.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Episode grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: '20px',
      }}>
        {sorted.map((ep, i) => (
          <EpisodeCard
            key={ep.id}
            episode={ep}
            onClick={() => onWatch(ep)}
            index={globalIndex + i}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Back to Home Button ─────────────────────────────────────────────────────

const BackToHomeButton: React.FC = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={() => navigate('/')}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: "'Bangers', cursive",
        fontSize: '1.1rem',
        letterSpacing: '0.14em',
        color: hovered ? '#0a0f1a' : '#FFD700',
        background: hovered ? '#FFD700' : 'transparent',
        border: '2px solid #FFD700',
        padding: '10px 28px',
        cursor: 'pointer',
        boxShadow: hovered
          ? '4px 4px 0 rgba(0,0,0,0.9), 0 0 24px rgba(255,215,0,0.45)'
          : '3px 3px 0 rgba(0,0,0,0.8), 0 0 12px rgba(255,215,0,0.15)',
        transition: 'color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <Home size={16} style={{ strokeWidth: 2.5 }} />
      BACK TO HOME
    </button>
  );
};

// ─── Empty State ─────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => (
  <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
    {/* Background */}
    <img
      src={ARTWORK.hero}
      alt=""
      aria-hidden
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        objectFit: 'cover',
        objectPosition: 'center top',
        filter: 'brightness(0.2) saturate(0.6)',
      }}
    />
    <div style={{
      position: 'absolute', inset: 0,
      background: 'linear-gradient(to top, #080d1f 0%, rgba(8,13,31,0.8) 50%, rgba(8,13,31,0.4) 100%)',
    }} />

    <Particles quantity={40} color="hsl(90, 100%, 54%)" />

    <div
      className="relative z-10 flex flex-col items-center justify-center text-center"
      style={{ minHeight: '100vh', padding: '80px 24px' }}
    >
      <Film size={64} style={{ color: 'rgba(124,252,0,0.4)', marginBottom: 24 }} />

      <h1 style={{
        fontFamily: "'Bangers', cursive",
        fontSize: 'clamp(3rem, 10vw, 6rem)',
        letterSpacing: '0.05em',
        lineHeight: 0.95,
        color: '#ffffff',
        textShadow: '3px 3px 0 rgba(0,0,0,0.8)',
        marginBottom: '20px',
      }}>
        COMING SOON
      </h1>

      <p style={{
        fontFamily: "'Bangers', cursive",
        fontSize: 'clamp(1.3rem, 3vw, 2rem)',
        letterSpacing: '0.12em',
        color: '#7CFC00',
        marginBottom: '16px',
      }}>
        DANGER DAN TV — ANIMATED SERIES
      </p>

      <p style={{
        fontFamily: "'Barlow', sans-serif",
        fontSize: 'clamp(1rem, 2vw, 1.2rem)',
        color: 'rgba(255,255,255,0.5)',
        maxWidth: '520px',
        lineHeight: 1.7,
        marginBottom: '40px',
      }}>
        An action-packed western fantasy cartoon series launching on Solana.
        Check back soon — episodes are loading in.
      </p>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: '40px',
      }}>
        <img
          src={ARTWORK.char1}
          alt=""
          aria-hidden
          style={{
            maxWidth: '480px',
            width: '80vw',
            objectFit: 'contain',
            opacity: 0.75,
            filter: 'saturate(0.8) brightness(0.9)',
          }}
        />
      </div>

      <BackToHomeButton />
    </div>
  </div>
);

// ─── Main SeriesPage ─────────────────────────────────────────────────────────

export const SeriesPage: React.FC = () => {
  const [activeModal, setActiveModal] = useState<EpisodesResponse | null>(null);

  const { data: allEpisodes } = useRealtimeData<EpisodesResponse[]>(
    subscribeManyEpisodes,
    true
  );

  // Policy already filters to published-only for unauthenticated viewers,
  // but we also filter client-side for safety
  const published = (allEpisodes ?? []).filter(ep => ep.published === true);

  // Sort by season, then episode number; newest season first
  const seasonMap = published.reduce<Record<number, EpisodesResponse[]>>((acc, ep) => {
    const s = ep.season as number;
    if (!acc[s]) acc[s] = [];
    acc[s].push(ep);
    return acc;
  }, {});

  const seasons = Object.keys(seasonMap)
    .map(Number)
    .sort((a, b) => b - a); // newest season first

  // Featured = highest season, highest episode number
  const featured: EpisodesResponse | null = seasons.length > 0
    ? [...(seasonMap[seasons[0]] ?? [])].sort((a, b) => (b.episodeNumber as number) - (a.episodeNumber as number))[0] ?? null
    : null;

  const handleWatch = useCallback((ep: EpisodesResponse) => {
    setActiveModal(ep);
  }, []);

  const handleCloseModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  if (published.length === 0) {
    return (
      <PageLayout fullBleed footer={false}>
        <SeriesNav />
        <EmptyState />
        {activeModal && <VideoModal episode={activeModal} onClose={handleCloseModal} />}
        <SeriesStyles />
      </PageLayout>
    );
  }

  // Pre-compute start index for each season to avoid mutation during render
  const seasonStartIndices = seasons.reduce<Record<number, number>>((acc, season, i) => {
    const prev = i === 0 ? 0 : acc[seasons[i - 1]] + (seasonMap[seasons[i - 1]]?.length ?? 0);
    acc[season] = prev;
    return acc;
  }, {});

  return (
    <PageLayout fullBleed footer={false}>
      <SeriesNav />

      {/* Hero */}
      <HeroFeatured episode={featured} onWatch={handleWatch} />

      {/* Episode browser */}
      <div
        style={{ background: '#080d1f', paddingTop: '60px', paddingBottom: '80px' }}
      >
        <div className="container">
          {/* Back to home */}
          <div style={{ marginBottom: '40px' }}>
            <BackToHomeButton />
          </div>

          {/* Section header */}
          <div style={{ marginBottom: '40px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <Film size={20} style={{ color: '#7CFC00' }} />
            <h2 style={{
              fontFamily: "'Bangers', cursive",
              fontSize: '1.5rem',
              letterSpacing: '0.15em',
              color: '#ffffff',
            }}>
              ALL EPISODES
            </h2>
            <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.06)' }} />
            <span style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.3)',
            }}>
              {published.length} episode{published.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Seasons */}
          {seasons.map(season => (
            <SeasonSection
              key={season}
              season={season}
              episodes={seasonMap[season] ?? []}
              onWatch={handleWatch}
              globalIndex={seasonStartIndices[season] ?? 0}
            />
          ))}
        </div>
      </div>

      {/* Character art strip */}
      <div style={{
        background: '#050a14',
        borderTop: '1px solid rgba(124,252,0,0.1)',
        padding: '40px 0',
        display: 'flex',
        justifyContent: 'center',
        gap: '40px',
        overflow: 'hidden',
      }}>
        {[ARTWORK.char1, ARTWORK.hero].map((src, i) => (
          <img
            key={i}
            src={src}
            alt=""
            aria-hidden
            style={{
              height: 120,
              objectFit: 'contain',
              opacity: 0.4,
              filter: 'saturate(0.5) brightness(0.7)',
            }}
          />
        ))}
      </div>

      {activeModal && <VideoModal episode={activeModal} onClose={handleCloseModal} />}
      <SeriesStyles />
    </PageLayout>
  );
};

// ─── Inline nav ───────────────────────────────────────────────────────────────

const SeriesNav: React.FC = () => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  return (
    <div style={{
      position: 'relative',
      zIndex: 20,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 40px',
      background: 'linear-gradient(to bottom, rgba(8,13,31,0.95) 0%, transparent 100%)',
    }}>
      <a
        href="/"
        style={{
          fontFamily: "'Bangers', cursive",
          fontSize: '1.4rem',
          letterSpacing: '0.08em',
          color: '#ffffff',
          textDecoration: 'none',
          textShadow: '2px 2px 0 rgba(0,0,0,0.8)',
        }}
      >
        <span style={{ color: '#7CFC00' }}>DANGER</span>{' '}
        <span style={{ color: '#E63946' }}>DAN</span>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', marginLeft: 8 }}>TV</span>
      </a>

      <button
        onClick={() => navigate('/')}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: "'Bangers', cursive",
          fontSize: '1rem',
          letterSpacing: '0.14em',
          color: hovered ? '#0a0f1a' : '#FFD700',
          background: hovered ? '#FFD700' : 'rgba(255,215,0,0.08)',
          border: '2px solid #FFD700',
          padding: '8px 22px',
          cursor: 'pointer',
          boxShadow: hovered
            ? '3px 3px 0 rgba(0,0,0,0.9), 0 0 20px rgba(255,215,0,0.4)'
            : '2px 2px 0 rgba(0,0,0,0.8), 0 0 8px rgba(255,215,0,0.1)',
          transition: 'color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
          transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        }}
      >
        <Home size={14} style={{ strokeWidth: 2.5 }} />
        BACK TO HOME
      </button>
    </div>
  );
};

// ─── CSS ─────────────────────────────────────────────────────────────────────

const SeriesStyles: React.FC = () => (
  <style>{`
    @keyframes bounce-fade {
      0%, 100% { opacity: 0.4; transform: translateY(0); }
      50% { opacity: 0.8; transform: translateY(4px); }
    }
  `}</style>
);

export default SeriesPage;
